import type { ReactNode, ComponentType } from "react";
import Tooltip from "./Tooltip";

/**
 * Props that components can extend to support tooltips
 */
export interface WithTooltipProps {
  tooltip?: ReactNode;
}

/**
 * Higher-order component that adds tooltip functionality to any component.
 * The wrapped component must accept a `ref` and forward it to a DOM element.
 *
 * @param Component - The component to wrap with tooltip functionality. Must accept a `ref` prop.
 * @returns Enhanced component with tooltip support.
 *
 * @example
 * ```tsx
 * interface MyComponentProps {
 *   label: string;
 *   onClick: () => void;
 *   // `ref` is automatically passed by this HOC.
 *   ref: React.Ref<HTMLButtonElement>;
 * }
 *
 * const MyComponent = ({ label, onClick, ref }: MyComponentProps) => (
 *   <button ref={ref} onClick={onClick}>{label}</button>
 * );
 *
 * export default withTooltip(MyComponent);
 * ```
 */
export function withTooltip<P extends object>(Component: ComponentType<P>): ComponentType<P & WithTooltipProps> {
  const WrappedComponent = ({ tooltip, ...props }: P & WithTooltipProps) => {
    const content = <Component {...(props as P)} />;

    if (tooltip) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>{content}</Tooltip.Trigger>
          <Tooltip.Content>{tooltip}</Tooltip.Content>
        </Tooltip.Root>
      );
    }

    return content;
  };

  WrappedComponent.displayName = `withTooltip(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

/**
 * Utility function to conditionally wrap content with a tooltip
 * Use this when you want inline tooltip wrapping without HOC
 *
 * @param content - The content to potentially wrap
 * @param tooltip - The tooltip content (if any)
 * @returns Content wrapped with tooltip if tooltip is provided, otherwise just content
 *
 * @example
 * ```tsx
 * function MyComponent({ tooltip, children }: { tooltip?: ReactNode; children: ReactNode }) {
 *   return wrapWithTooltip(
 *     <div>{children}</div>,
 *     tooltip
 *   );
 * }
 * ```
 */
export function wrapWithTooltip(content: ReactNode, tooltip?: ReactNode): ReactNode {
  if (tooltip) {
    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{content}</Tooltip.Trigger>
        <Tooltip.Content>{tooltip}</Tooltip.Content>
      </Tooltip.Root>
    );
  }

  return content;
}
